import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Timeline, Issues, Commitments, Diligence, FundsFlow, Signature, Closing,
} from "../common/AgentResult";
import type { ActionLog } from "../../types";

type Entry = Record<string, unknown>;
/** wsId → tracker state key → accumulated entries (orchestrator TrackerStateMap). */
export type TrackersMap = Record<string, Record<string, Entry[]>>;

export interface TrackersPanelProps {
  /** Accumulated tracker state from the live orchestrator, when available. */
  trackers?: TrackersMap | null;
  /** Fallback source: per-agent action logs (already filtered to active agents). */
  byAgent: Record<string, ActionLog[]>;
  routedWs: (emailId: number) => string;
  activeWs: string;
  setActiveWs: (id: string) => void;
  wsDefs: Array<{ id: string; name: string }>;
  activeAgents: Set<string>;
  trackerKeys?: Set<string>;
}

interface SectionDef {
  /** Tracker agent check id. */
  id: string;
  label: string;
  /** Key in the orchestrator's per-workstream tracker state. */
  stateKey: string;
  /** Array key in the agent's per-email resultJson. */
  resultKey: string;
  /** Fields that identify an entry for update-else-append merge. */
  matchOn: string[];
  R: (p: { r: Entry }) => JSX.Element | null;
}

/* Mirrors TRACKER_KINDS in api/src/services/trackerState.ts; order is the
   product-spec section order, not the roster order. */
const SECTIONS: SectionDef[] = [
  { id: "timeline", label: "Timeline", stateKey: "timeline", resultKey: "entries", matchOn: ["date", "event"], R: Timeline },
  { id: "issues", label: "Issues", stateKey: "issues", resultKey: "issues", matchOn: ["issue"], R: Issues },
  { id: "commitments", label: "Commitments", stateKey: "commitments", resultKey: "commitments", matchOn: ["who", "what"], R: Commitments },
  { id: "diligence", label: "Diligence", stateKey: "diligence", resultKey: "requests", matchOn: ["item"], R: Diligence },
  { id: "fundsflow", label: "Wires", stateKey: "wires", resultKey: "wires", matchOn: ["from", "to"], R: FundsFlow },
  { id: "signature", label: "Signatures", stateKey: "signatures", resultKey: "signatures", matchOn: ["document", "party"], R: Signature },
  { id: "closing", label: "Closing", stateKey: "closingItems", resultKey: "items", matchOn: ["condition"], R: Closing },
];

function normKey(e: Entry, fields: string[]): string {
  return fields
    .map((f) => String(e[f] ?? "").trim().toLowerCase().replace(/\s+/g, " "))
    .join("|");
}

/* Client-side update-else-append merge (mergeTracker without history), used
   when the run carries no accumulated orchestrator state (seeded replays). */
function mergeEntries(prior: Entry[], emitted: unknown, matchOn: string[]): Entry[] {
  if (!Array.isArray(emitted)) return prior;
  const out = prior.slice();
  const index = new Map<string, number>(out.map((e, i) => [normKey(e, matchOn), i] as [string, number]));
  for (const raw of emitted) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const e = raw as Entry;
    const k = normKey(e, matchOn);
    if (!k.replace(/\|/g, "").trim()) continue;
    const at = index.get(k);
    if (at === undefined) {
      index.set(k, out.length);
      out.push({ ...e });
    } else {
      const patch: Entry = {};
      for (const f of Object.keys(e)) if (e[f] != null) patch[f] = e[f];
      out[at] = { ...out[at], ...patch };
    }
  }
  return out;
}

/* The "Trackers" tab — per-workstream accumulated state of the seven tracker
   agents, one collapsible section each, rendered with the same structured
   renderers the agents use elsewhere. */
export function TrackersPanel(p: TrackersPanelProps): JSX.Element {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const ws =
    p.activeWs === "unassoc" && p.wsDefs.length ? p.wsDefs[0].id : p.activeWs;

  const data = useMemo(() => {
    const out: Record<string, { entries: Entry[]; disc?: Entry[] }> = {};
    const wsLogs = (id: string): ActionLog[] =>
      (p.byAgent[id] || []).filter(
        (a) => a.emailId != null && p.routedWs(a.emailId) === ws,
      );
    for (const s of SECTIONS) {
      const live = p.trackers && p.trackers[ws] && p.trackers[ws][s.stateKey];
      let entries: Entry[];
      if (Array.isArray(live) && live.length) {
        entries = live;
      } else {
        entries = [];
        for (const a of wsLogs(s.id)) {
          entries = mergeEntries(entries, a.result && a.result[s.resultKey], s.matchOn);
        }
      }
      out[s.id] = { entries };
    }
    /* Wire discrepancies are per-email findings, not accumulated tracker
       state — always aggregate them from the funds-flow logs so the
       fraud-sensitive rows stay visible. */
    let disc: Entry[] = [];
    for (const a of wsLogs("fundsflow")) {
      disc = mergeEntries(disc, a.result && a.result.discrepancies, ["detail"]);
    }
    out.fundsflow.disc = disc;
    return out;
  }, [p.trackers, p.byAgent, p.routedWs, ws]);

  if (!p.wsDefs.length) {
    return <div className="empty">No workstreams in scope.</div>;
  }
  return (
    <div>
      <div className="mhd">
        <select
          className="sel"
          value={ws}
          onChange={(e) => p.setActiveWs(e.target.value)}
          style={{ width: "100%" }}
        >
          {p.wsDefs.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </div>
      <div className="tert" style={{ fontSize: 11.5, marginBottom: 10, lineHeight: 1.5 }}>
        The accumulated state each tracker agent keeps for this workstream,
        updated as emails are processed.
      </div>
      {SECTIONS.map((s) => {
        const off = p.trackerKeys ? !p.trackerKeys.has(s.stateKey) : !p.activeAgents.has(s.id);
        const d = data[s.id];
        const count = d.entries.length + (d.disc ? d.disc.length : 0);
        const open = !collapsed[s.id];
        const result: Entry =
          s.id === "fundsflow"
            ? { wires: d.entries, discrepancies: d.disc || [] }
            : { [s.resultKey]: d.entries };
        return (
          <div className="trksec" key={s.id}>
            <button
              className="trkhd"
              onClick={() => setCollapsed((c) => ({ ...c, [s.id]: !c[s.id] }))}
              aria-expanded={open}
            >
              {open ? (
                <ChevronDown size={13} color="var(--tert)" />
              ) : (
                <ChevronRight size={13} color="var(--tert)" />
              )}
              <span className="up">{s.label}</span>
              <span className="mono tert" style={{ fontSize: 11, textTransform: "none", letterSpacing: 0 }}>
                · {off ? "hidden" : `${count} ${count === 1 ? "entry" : "entries"}`}
              </span>
            </button>
            {open && (
              <div className="trkbody">
                {off ? (
                  <div className="tert" style={{ fontSize: 11.5, padding: "2px 0 4px" }}>
                    Agent output hidden — toggle it on in the Actions tab.
                  </div>
                ) : count === 0 ? (
                  <div className="tert" style={{ fontSize: 11.5, padding: "2px 0 4px" }}>
                    Nothing tracked yet for this workstream.
                  </div>
                ) : (
                  <s.R r={result} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
